'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href: string;
}

// Route label map — add new routes here
const ROUTE_LABELS: Record<string, string> = {
  'call-dashboard': 'Dashboard',
  'call-history': 'Storico Chiamate',
  'call-detail': 'Dettaglio Chiamata',
  'call-management': 'Gestione Chiamate',
  'call-recordings': 'Registrazioni',
  'call-whisper': 'Whisper / Barge',
  'patient-management': 'Gestione Pazienti',
  'patient-portal': 'Portale Paziente',
  'patient-threads': 'Thread Pazienti',
  'patients': 'Pazienti',
  'follow-ups': 'Follow-up',
  'followup-automation': 'Automazione Follow-up',
  'callbacks': 'Callback',
  'upcoming': 'Futuri',
  'operators': 'Operatori',
  'operator-dashboard': 'La Mia Dashboard',
  'operator-settings': 'Impostazioni Operatore',
  'operator-profile': 'Profilo Operatore',
  'operator-comparison': 'Confronto Operatori',
  'operator-leaderboard': 'Classifica Operatori',
  'operator-sla': 'SLA Operatori',
  'operator-chat': 'Chat Operatori',
  'analytics': 'Analytics',
  'telecom': 'Telecom',
  'funnel': 'Funnel',
  'cohorts': 'Coorti',
  'revenue': 'Revenue',
  'sms': 'SMS',
  'epidemiology': 'Epidemiologia',
  'exams': 'Esami',
  'custom': 'Personalizzato',
  'settings': 'Impostazioni',
  'alert-notifications': 'Alert & Notifiche',
  'whatsapp-notifications': 'Notifiche WhatsApp',
  'calendar': 'Calendario',
  'results': 'Referti',
  'critical': 'Critici',
  'ai-insights': 'AI Insights',
  'history': 'Storico',
  'reports': 'Report',
  'scheduled': 'Schedulati',
  'sla': 'SLA',
  'lab': 'Laboratorio',
  'integrations': 'Integrazioni',
  'profile': 'Profilo',
  'team': 'Team',
  'roles': 'Ruoli',
  'webhooks': 'Webhook',
  'lis': 'LIS',
  'reminders': 'Promemoria',
  'api': 'API',
  'hours': 'Orari',
  'notifications': 'Notifiche',
  'sms-templates': 'Template SMS',
  'data-retention': 'Conservazione Dati',
  'upgrade': 'Upgrade',
  'superadmin': 'Super Admin',
  'tenants': 'Tenant',
  'audit': 'Audit',
  'email': 'Email',
  'features': 'Feature Flags',
  'provisioning': 'Provisioning',
  'coupons': 'Coupon',
  'announcements': 'Annunci',
  'migrations': 'Migrazioni',
  'rate-limits': 'Rate Limits',
  'health': 'Salute Sistema',
  'twilio': 'Twilio',
  'twilio-flex': 'Twilio Flex',
  'twilio-health': 'Twilio Health',
  'twilio-notify': 'Notifiche Operatori',
  'voice-intelligence': 'Voice Intelligence',
  'numbers-management': 'Gestione Numeri',
  'unified-conversations': 'Conversazioni Unificate',
  'verify-security': 'Verifica Sicurezza',
  'verify-otp': 'Verifica OTP',
  'security': 'Sicurezza',
  '2fa': 'Autenticazione 2FA',
  'ivr': 'IVR',
  'builder': 'Builder',
  'scripts': 'Script',
  'campaigns': 'Campagne',
  'new': 'Nuovo',
  'exam-catalog': 'Catalogo Esami',
  'packages': 'Pacchetti',
  'exam-prep': 'Preparazione Esami',
  'prescriptions': 'Prescrizioni',
  'consents': 'Consensi',
  'intake': 'Accettazione',
  'queue': 'Coda',
  'kiosk': 'Totem Kiosk',
  'samples': 'Campioni',
  'urgent': 'Urgenze STAT',
  'home-collection': 'Prelievo a Domicilio',
  'schedule': 'Pianificazione',
  'physicians': 'Medici',
  'stats': 'Statistiche',
  'portal': 'Portale',
  'billing-advanced': 'Fatturazione Avanzata',
  'exemptions': 'Esenzioni',
  'tariffs': 'Tariffe',
  'ssn-report': 'Report SSN',
  'agreements': 'Convenzioni',
  'quality': 'Qualità',
  'veq': 'VEQ',
  'branches': 'Multi-sede',
  'dashboard': 'Dashboard',
  'availability-grid': 'Griglia Disponibilità',
  'weekly-availability': 'Disponibilità Settimanale',
  'shifts': 'Turni',
  'shift-swap': 'Scambio Turni',
  'worked-hours': 'Ore Lavorate',
  'daily-goals': 'Obiettivi Giornalieri',
  'goals': 'Obiettivi',
  'config': 'Configurazione',
  'wallboard': 'Wallboard',
  'supervisor': 'Supervisore',
  'supervisor-broadcast': 'Broadcast Supervisore',
  'session-replay': 'Session Replay',
  'handover-history': 'Storico Handover',
  'support': 'Supporto',
  'status': 'Stato Sistema',
  'help': 'Aiuto',
  'videos': 'Video',
  'kb': 'Knowledge Base',
  'blog': 'Blog',
  'about': 'Chi Siamo',
  'pricing': 'Prezzi',
  'terms': 'Termini',
  'privacy': 'Privacy',
  'cookie-policy': 'Cookie Policy',
  'changelog': 'Changelog',
  'export': 'Esporta',
  'data-import': 'Importa Dati',
  'blacklist': 'Blacklist',
  'rules': 'Regole',
  'phone-lookup': 'Ricerca Numero',
  'voicemail': 'Voicemail',
  'video-consultation': 'Videoconsulto',
  'invoices': 'Fatture',
  'referrals': 'Referral',
  'audit-trail': 'Audit Trail',
  'mobile-dashboard': 'Dashboard Mobile',
  'studio-manager': 'Studio Manager',
  'calls': 'Chiamate',
  'conversations': 'Conversazioni',
  'onboarding': 'Onboarding',
  'wizard': 'Wizard',
  'checklist': 'Checklist',
  'lab-onboarding': 'Onboarding Lab',
  'cal-agenda': 'Agenda Cal.com',
  'verify-email': 'Verifica Email',
  'edit': 'Modifica',
  'duplicates': 'Duplicati',
  'merge': 'Unisci',
  'upload': 'Carica',
  'bulk': 'Bulk',
  'login': 'Login',
  'register': 'Registrazione',
  'forgot-password': 'Password Dimenticata',
  'reset-password': 'Reset Password',
  'confirm': 'Conferma',
  'invite': 'Invito',
};

function getLabel(segment: string): string {
  // Check if it's a UUID or numeric ID
  if (/^[0-9a-f-]{36}$/.test(segment) || /^\d+$/.test(segment)) {
    return `#${segment.slice(0, 8)}`;
  }
  return ROUTE_LABELS[segment] ?? segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function Breadcrumb() {
  const pathname = usePathname();

  if (!pathname || pathname === '/call-dashboard') return null;

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const items: BreadcrumbItem[] = [
    { label: 'Home', href: '/call-dashboard' },
  ];

  segments.forEach((seg, i) => {
    items.push({
      label: getLabel(seg),
      href: '/' + segments.slice(0, i + 1).join('/'),
    });
  });

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 px-6 lg:px-8 py-2 text-xs text-muted-foreground border-b border-border/50 bg-background/50">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <React.Fragment key={item.href}>
            {i === 0 ? (
              <Link
                href={item.href}
                className="flex items-center gap-1 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                aria-label="Vai alla home"
              >
                <Home size={11} aria-hidden="true" />
              </Link>
            ) : isLast ? (
              <span className="text-foreground font-medium truncate max-w-[200px]" aria-current="page">{item.label}</span>
            ) : (
              <Link
                href={item.href}
                className="hover:text-foreground transition-colors truncate max-w-[150px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
              >
                {item.label}
              </Link>
            )}
            {!isLast && <ChevronRight size={10} className="shrink-0" aria-hidden="true" />}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
